const DefaultEventEnum = {
  NO_PONG: 'noPong', // ping之後未收到pong回應
  DEFAULT_ERROR: 'defaultError', // 未handle的錯誤
};

const SendEventEnum = {
  PING: 'ping',
};

// 後端event對應constant
const ResponseEventEnum = {
  REGISTER_SUCCESSFULLY: 'registerSuccessfully',
  MESSAGE: 'message',
  ASK_IF_LOGOUT_OTHER_ACCOUNT: 'askIfLogoutOtherAccount',
  LOGOUT: 'logout',
  REDIRECT_ROUTER: 'redirectRouter',
  UPDATE_SUCCESSFULLY: 'updateSuccessfully',
  UPDATE_ENV: 'updateEnv',
  ERROR: 'error',
  PONG: 'pong',
};

class Socket {
  constructor() {
    this.isInit = false;
    this.socketUrl = null;
    this.DefaultEventEnum = DefaultEventEnum;
    this.ResponseEventEnum = ResponseEventEnum;
    this.SendEventEnum = SendEventEnum;
    this.ping = 9999;
  }

  addDefaultEventEnum(newEventEnum) {
    this.DefaultEventEnum = {
      ...this.DefaultEventEnum,
      ...newEventEnum,
    };
  }
  addResponseEventEnum(newEventEnum) {
    this.ResponseEventEnum = {
      ...this.ResponseEventEnum,
      ...newEventEnum,
    };
  }
  addSendEventEnum(newEventEnum) {
    this.SendEventEnum = {
      ...this.SendEventEnum,
      ...newEventEnum,
    };
  }

  init(router) {
    this.isInit = true;
    this.router = router;
    this.isReconnect = true;
    this.isReady = false;
    this.keepAliveTimeout = null; // 保持連線避免idle的timeout id
    this.isPong = false; // 是否收到pong的回應
    this.pingPongTimer = null;
    this.pongTimeout = null;

    this.waitingQueue = []; // 暫存還沒socket還沒open之前就發出的payload
    this.waitingActionNames = [];
    this.waitingEvents = [];
    this.cbMap = {};
    this.onceCbMap = {};
    this.errorCbMap = {};
    this.onRouteMap = {};

    this.onOpenBind = this.register.bind(this);
    this.onMessageBind = this.socketMessageCallback.bind(this);
    this.onCloseBind = this.socketCloseCallback.bind(this);
    this.onErrorBind = this.socketErrorCallback.bind(this);

    for (const eventName in this.ResponseEventEnum) {
      this.cbMap[this.ResponseEventEnum[eventName]] = [];
      this.onceCbMap[this.ResponseEventEnum[eventName]] = [];
      this.errorCbMap[this.ResponseEventEnum[eventName]] = [];
    }
    for (const eventName in this.SendEventEnum) {
      this.errorCbMap[this.SendEventEnum[eventName]] = [];
    }
    for (const eventName in this.DefaultEventEnum) {
      this.errorCbMap[this.DefaultEventEnum[eventName]] = [];
    }

    this.connect();
  }

  connect() {
    this.socket = new WebSocket(this.socketUrl);
    this.recoverEventListener();

    this.socket.addEventListener('open', this.onOpenBind);
    this.socket.addEventListener('message', this.onMessageBind);
    this.socket.addEventListener('close', this.onCloseBind);
    this.socket.addEventListener('error', this.onErrorBind);
  }

  recoverEventListener() {
    Object.keys(this.cbMap).forEach((key) => {
      this.cbMap[key].forEach((cb) => this.socket.addEventListener(key, cb));
    });
    Object.keys(this.onceCbMap).forEach((key) => {
      this.onceCbMap[key].forEach((cb) =>
        this.socket.addEventListener(key, cb)
      );
    });
    Object.keys(this.errorCbMap).forEach((key) => {
      this.errorCbMap[key].forEach((cb) =>
        this.socket.addEventListener(key, cb)
      );
    });
  }

  register() {
    this.send(
      {
        action: 'register',
        router: this.router,
        ux: {
          userAgent: navigator.userAgent,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight,
          outerWidth: window.outerWidth,
          outerHeight: window.outerHeight,
        },
      },
      true
    );
  }

  update(data = {router: this.router}) {
    if (data.router) {
      this.router = data.router;
    }

    this.send({
      action: 'update',
      data,
    });
  }

  startPingPong() {
    let sendTime = 0;
    this.endPingPong();
    this.on(this.ResponseEventEnum.PONG, () => {
      const receiveTime = new Date().getTime();
      const ping = receiveTime - sendTime;
      this.ping = parseInt(ping);
      this.isPong = true;
    });

    /**
     * 發射 ping pong
     * @returns {Function} pingPong
     */
    const pingPong = () => {
      sendTime = new Date().getTime();
      this.isPong = false;
      this.send({
        action: this.SendEventEnum.PING,
      });

      this.pongTimeout = setTimeout(() => {
        if (!this.isPong) {
          this.endPingPong();
          this.destroy(true);
          this.errorCbMap[this.DefaultEventEnum.NO_PONG].forEach((cb) => cb());
          this.connect();
        }
      }, 1000 * 2);
      return pingPong;
    };

    this.pingPongTimer = setInterval(pingPong(), 1000 * 10);
  }

  endPingPong() {
    clearInterval(this.pingPongTimer);
    clearTimeout(this.pongTimeout);
    this.clearEvent(this.ResponseEventEnum.PONG);
  }

  onAskIfLogoutOtherAccount(cb) {
    this.socket.addEventListener(
      this.ResponseEventEnum.ASK_IF_LOGOUT_OTHER_ACCOUNT,
      cb
    );
    this.cbMap[this.ResponseEventEnum.ASK_IF_LOGOUT_OTHER_ACCOUNT].push(cb);
  }

  onRedirectRouter(cb) {
    this.socket.addEventListener(this.ResponseEventEnum.REDIRECT_ROUTER, cb);
    this.cbMap[this.ResponseEventEnum.REDIRECT_ROUTER].push(cb);
  }

  onLogout(cb) {
    this.socket.addEventListener(this.ResponseEventEnum.LOGOUT, cb);
    this.cbMap[this.ResponseEventEnum.LOGOUT].push(cb);
  }

  onUpdateEnv(cb) {
    this.socket.addEventListener(this.ResponseEventEnum.UPDATE_ENV, cb);
    this.cbMap[this.ResponseEventEnum.UPDATE_ENV].push(cb);
  }

  on(eventName, cb) {
    if (Array.isArray(eventName)) {
      eventName.forEach((name) => {
        this.cbMap[name].push(cb);
      });
    } else {
      if (!this.cbMap[eventName]) this.cbMap[eventName] = [];
      this.cbMap[eventName].push(cb);
    }
  }

  once(eventName, cb) {
    if (!this.onceCbMap[eventName]) this.onceCbMap[eventName] = [];
    this.onceCbMap[eventName].push(cb);
  }

  // 限定在特定 route 才會執行的 callback，不會被 clearEvent / clearEvents 清除
  onRoute(eventName, routeName, cb) {
    if (!this.onRouteMap[routeName]) {
      this.onRouteMap[routeName] = {};
    }
    this.onRouteMap[routeName][eventName] = cb;
  }

  onError(eventName, cb) {
    if (!this.errorCbMap[eventName]) this.errorCbMap[eventName] = [];
    this.errorCbMap[eventName].push(cb);
  }

  execRouteCb(eventName, data) {
    if (this.onRouteMap[this.router]?.[eventName]) {
      this.onRouteMap[this.router][eventName](data);
    }
  }

  sendLogoutOtherAccount() {
    this.send({
      action: 'logoutOtherAccount',
    });
  }

  send(payload, isForce) {
    let payloadString = payload;
    if (typeof payload === 'object') {
      payloadString = JSON.stringify(payload);
    }
    if (this.isReady || isForce) {
      this.socket.send(payloadString);
      // 10分鐘idle，lambda會自動斷線。所以定時ping一下，避免其idle
      clearTimeout(this.keepAliveTimeout);
      this.keepAliveTimeout = setTimeout(() => {
        this.update();
      }, 1000 * 60 * 9);
    } else if (!this.isReady) {
      const isDuplicated = this.waitingQueue.some(
        (queuePayload) => payloadString == queuePayload
      );
      if (!isDuplicated) {
        this.waitingQueue.push(payloadString);
      }
      // console.log('- Socket - Preserve payload', payload);
    } else {
      console.log('- Socket - update error', payload);
    }
  }

  // 清除 socket 事件監聽，只清除 on、once、onError，不清除 onRoute
  clearEvent(event) {
    this.cbMap[event] = [];
    this.onceCbMap[event] = [];
    this.errorCbMap[event] = [];
  }

  // 批量清除 socket 事件監聽，只清除 on、once、onError，不清除 onRoute
  clearEvents(events) {
    events.forEach((event) => {
      this.cbMap[event] = [];
      this.onceCbMap[event] = [];
      this.errorCbMap[event] = [];
    });
  }

  // 清除 socket 事件監聽，只清除 onRoute
  clearRouteEvent(routeName) {
    this.onRouteMap[routeName] = {};
  }

  beforeSocketMessageCallback(event, isWaitingRun) {
    const data = JSON.parse(event.data);
    const {action} = data;
    if (!isWaitingRun && this.waitingActionNames?.includes(action)) {
      this.waitingEvents.push(event);
      return false;
    }
    return true;
  }

  socketMessageCallback(event, isWaitingRun) {
    const data = JSON.parse(event.data);
    const {action} = data;
    if (!isWaitingRun && this.waitingActionNames?.includes(action)) {
      return this.waitingEvents.push(event);
    }

    switch (data.action) {
      case this.ResponseEventEnum.REGISTER_SUCCESSFULLY:
        this.isReady = true;
        for (const payload of this.waitingQueue) {
          this.send(payload);
        }
        this.waitingQueue = [];

        this.cbMap[data.action]?.forEach((cb) => cb(data));
        this.execRouteCb(data.action, data);
        this.onceCbMap[data.action]?.forEach((cb) => cb(data));
        this.onceCbMap[data.action] = [];
        break;
      case this.ResponseEventEnum.ASK_IF_LOGOUT_OTHER_ACCOUNT:
        if (!window.localStorage.getItem('testMode')) {
          this.cbMap[
            this.ResponseEventEnum.ASK_IF_LOGOUT_OTHER_ACCOUNT
          ]?.forEach((cb) => cb(data));
        }
        break;
      case this.ResponseEventEnum.LOGOUT:
      case this.ResponseEventEnum.REDIRECT_ROUTER:
      case this.ResponseEventEnum.PONG:
      case this.ResponseEventEnum.UPDATE_ENV:
        this.cbMap[data.action]?.forEach((cb) => cb(data));
        break;
      case this.ResponseEventEnum.ERROR:
        if (this.errorCbMap[data.requestAction].length === 0) {
          this.errorCbMap[this.DefaultEventEnum.DEFAULT_ERROR]?.forEach((cb) =>
            cb(data)
          );
        } else {
          this.errorCbMap[data.requestAction].forEach((cb) => cb(data));
        }
        break;
      case this.ResponseEventEnum.UPDATE_SUCCESSFULLY:
        this.execRouteCb(data.action, data);
        this.onceCbMap[data.action]?.forEach((cb) => cb(data));
        this.onceCbMap[data.action] = [];
        break;
      default:
        throw new Error('Unknown event from server');
    }
  }

  socketCloseCallback() {
    this.isReady = false;
    if (this.isReconnect) {
      setTimeout(() => {
        this.connect();
      }, 1000);
    }
  }

  socketErrorCallback(err) {
    console.error('socket error', err);
  }

  destroy(isReconnect = false) {
    this.isReconnect = isReconnect;
    if (this.socket) {
      this.socket.removeEventListener('open', this.onOpenBind);
      this.socket.removeEventListener('message', this.onMessageBind);
      this.socket.removeEventListener('close', this.onCloseBind);
      this.socket.removeEventListener('error', this.onErrorBind);
      this.socket.close();
    }
    this.isInit = isReconnect;
  }
  startWaitingEvents(actionNames) {
    if (Array.isArray(actionNames)) {
      this.waitingActionNames.push(...actionNames);
    } else {
      this.waitingActionNames.push(actionNames);
    }
  }
  runWaitingEvents() {
    let index = 0;
    while (index < this.waitingEvents.length) {
      const event = this.waitingEvents[index];
      this.socketMessageCallback(event, true);
      index += 1;
    }
    this.waitingEvents = [];
  }
  stopWaitingEvents() {
    this.waitingEvents = [];
    this.waitingActionNames = [];
  }
}

export default Socket;
